import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { useNavigate } from 'react-router-dom';

import { H1, H2, H3 } from '@decisiv/ui-components/lib/components/Typography';
import Search from '@decisiv/iconix/lib/components/Search';
import Rocket from '@decisiv/iconix/lib/components/Rocket';
import LinkIcon from '@decisiv/iconix/lib/components/Link';

import Helmet from '../../components/SEO/Helmet';
import CopyValueButton from '../../components/CopyValueButton';
import IntegrationLambdasApi from '../../api/integration_lambdas';
import authorizeOn from '../../utils/resourceAuthorizer';
import { EndpointUrl } from './style';
import '../ActionsPage/actions-toolkit.css';

// DIL paths are `<action>/<version>/<provider>/execute` and the API names them
// "Action - Provider"; group by action, listing each provider's endpoint.
const actionLabel = (name = '') => name.split(' - ')[0] || name;
const providerLabel = (name = '') =>
  name.split(' - ').slice(1).join(' - ') || name;

// The action's leading verb (create/get/retrieve/…) drives the filter chips.
const verbOf = (group) => (group.id || '').split(/[-_]/)[0];
const capitalize = (word = '') => word.charAt(0).toUpperCase() + word.slice(1);

// DIL action labels are prose (e.g. "Retrieve Campaign Information"), not the
// snake_case identifiers the toolkit CSS assumes, so the mono row/title styling
// is swapped back to the sans family. URLs stay monospace (see EndpointUrl).
const proseFont = { fontFamily: 'var(--kds-font)' };

const dilShape = PropTypes.shape({
  id: PropTypes.string,
  attributes: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
  }),
});

const groupShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  endpoints: PropTypes.arrayOf(dilShape).isRequired,
});

function Toolbar({ search, onSearch, verbs, activeVerb, onVerb }) {
  return (
    <div className="page-toolbar">
      <div className="page-title-row">
        <H1 className="page-title">{t`Decisiv Integration Lambda`}</H1>
      </div>
      <p
        style={{
          margin: '0 0 14px',
          maxWidth: 720,
          fontSize: 13,
          lineHeight: 1.5,
          color: 'var(--kds-text-3)',
        }}
      >
        {t`Decisiv Integration Lambdas (DILs) are prebuilt endpoints for common integration tasks. Copy a DIL URL below into an integration's "Http Request URL" global setting to call it.`}
      </p>
      <div className="toolbar-row">
        <div className="search-box">
          <span className="icon">
            <Search size="small" />
          </span>
          <input
            type="search"
            placeholder={t`Search DILs by action or provider…`}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
        {verbs.length > 1 && (
          <>
            <span className="filter-label">{t`Filter by:`}</span>
            <div className="chips">
              {verbs.map((verb) => (
                <button
                  type="button"
                  key={verb.key}
                  className={`chip ${activeVerb === verb.key ? 'active' : ''}`}
                  style={{ paddingLeft: 10 }}
                  onClick={() => onVerb(verb.key)}
                >
                  {verb.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

Toolbar.propTypes = {
  search: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  verbs: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string }),
  ).isRequired,
  activeVerb: PropTypes.string.isRequired,
  onVerb: PropTypes.func.isRequired,
};

function ListPane({ groups, selectedId = null, onSelect }) {
  return (
    <aside className="list-pane">
      <div className="list-header">
        <span className="count">
          {groups.length} {groups.length === 1 ? t`action` : t`actions`}
        </span>
      </div>
      <div className="list-scroll">
        {groups.length === 0 && (
          <div
            style={{
              padding: '32px 20px',
              textAlign: 'center',
              color: 'var(--kds-text-3)',
              fontSize: 13,
            }}
          >
            {t`No DILs match your search.`}
          </div>
        )}
        {groups.map((group) => (
          <div
            key={group.id}
            className={`action-row ${
              selectedId === group.id ? 'selected' : ''
            }`}
            style={{ '--row-color': 'var(--kds-accent)', padding: '7px 14px' }}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(group.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(group.id);
              }
            }}
          >
            <span className="row-icon">
              <Rocket size="small" />
            </span>
            <div className="row-body">
              <div className="row-name" style={proseFont}>
                {group.name}
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 12,
                    fontWeight: 400,
                    color: 'var(--kds-text-3)',
                  }}
                >
                  {group.endpoints.length}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

ListPane.propTypes = {
  groups: PropTypes.arrayOf(groupShape).isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};
function DetailPane({ group }) {
  return (
    <section className="detail-pane">
      <div className="detail-header" style={{ paddingBottom: 16 }}>
        <div className="head-top">
          <div className="head-left">
            <H2 className="action-name" style={proseFont}>
              {group.name}
            </H2>
            <div className="alias">
              {group.endpoints.length}{' '}
              {group.endpoints.length === 1 ? t`endpoint` : t`endpoints`}
            </div>
          </div>
        </div>
      </div>
      <div className="detail-body">
        <div className="detail-section">
          <H3>{t`Endpoints`}</H3>
          <div className="int-list">
            {group.endpoints.map((endpoint) => {
              const url = endpoint.attributes?.url || '';
              return (
                <div className="int-row" key={endpoint.id}>
                  <span className="int-icon">
                    <LinkIcon size="medium" />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="int-name">
                      {providerLabel(endpoint.attributes?.name)}
                    </div>
                    <div style={{ marginTop: 3 }}>
                      <EndpointUrl>{url}</EndpointUrl>
                    </div>
                  </div>
                  <CopyValueButton value={url} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

DetailPane.propTypes = {
  group: groupShape.isRequired,
};

function EmptyDetail() {
  return (
    <section className="detail-pane">
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--kds-text-3)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <Rocket size="medium" color="var(--if-ink-3)" />
          <div style={{ marginTop: 6 }}>
            {t`No Decisiv Integration Lambdas available.`}
          </div>
        </div>
      </div>
    </section>
  );
}

const SKELETON_ROWS = Array.from({ length: 8 }, (_, i) => `skel-row-${i}`);
const SKELETON_ENDPOINTS = ['skel-ep-0', 'skel-ep-1', 'skel-ep-2'];
const SKELETON_CHIPS = [84, 70, 90, 96, 110];

function SkeletonView() {
  return (
    <div className="at-root">
      <Helmet
        title={t`Decisiv Integration Lambda`}
        description={t`Decisiv Integration Lambda`}
      />
      <div className="page">
        <div className="page-toolbar">
          <div className="page-title-row">
            <H1 className="page-title">{t`Decisiv Integration Lambda`}</H1>
          </div>
          <span
            className="at-skel"
            style={{
              width: '60%',
              maxWidth: 640,
              height: 12,
              margin: '4px 0 14px',
            }}
          />
          <div className="toolbar-row">
            <span className="at-skel" style={{ width: 280, height: 36 }} />
            <span
              className="at-skel"
              style={{ width: 60, height: 12, marginLeft: 12 }}
            />
            {SKELETON_CHIPS.map((width) => (
              <span
                key={width}
                className="at-skel"
                style={{ width, height: 24, borderRadius: 12 }}
              />
            ))}
          </div>
        </div>
        <div className="body">
          <div className="list-pane">
            <div className="list-header">
              <span className="at-skel" style={{ width: 60, height: 11 }} />
            </div>
            <div className="list-scroll">
              {SKELETON_ROWS.map((key, i) => (
                <div key={key} className="at-skel-row">
                  <span
                    className="at-skel"
                    style={{ width: 24, height: 24, borderRadius: 6 }}
                  />
                  <div className="at-skel-row-info">
                    <span
                      className="at-skel"
                      style={{ width: `${50 + ((i * 9) % 35)}%`, height: 12 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="at-skel-detail">
            <div className="at-skel-detail-head">
              <span className="at-skel" style={{ width: 220, height: 22 }} />
              <span
                className="at-skel"
                style={{ width: 48, height: 22, borderRadius: 12 }}
              />
            </div>
            <div className="at-skel-section">
              <span className="at-skel" style={{ width: 90, height: 11 }} />
              {SKELETON_ENDPOINTS.map((key) => (
                <span
                  key={key}
                  className="at-skel"
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 52,
                    marginTop: 10,
                    borderRadius: 8,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function IntegrationLambdasPage() {
  const navigate = useNavigate();
  const [integrationLambdas, setIntegrationLambdas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeVerb, setActiveVerb] = useState('all');
  const [selectedId, setSelectedId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const result = await IntegrationLambdasApi.fetchIntegrationLambdas();

    if (result.data) {
      setIntegrationLambdas(result.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!authorizeOn()) {
      navigate('/integrations');
      return;
    }
    fetchData();
  }, [fetchData]);

  const groups = useMemo(() => {
    const byAction = new Map();

    integrationLambdas.forEach((dil) => {
      const action = (dil.id || '').split('/')[0];

      if (!byAction.has(action)) {
        byAction.set(action, {
          id: action,
          name: actionLabel(dil.attributes?.name),
          endpoints: [],
        });
      }

      byAction.get(action).endpoints.push(dil);
    });

    return [...byAction.values()];
  }, [integrationLambdas]);

  const verbs = useMemo(() => {
    const present = [...new Set(groups.map(verbOf).filter(Boolean))].sort();
    return [
      { key: 'all', label: t`All` },
      ...present.map((verb) => ({ key: verb, label: capitalize(verb) })),
    ];
  }, [groups]);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return groups.filter((group) => {
      if (activeVerb !== 'all' && verbOf(group) !== activeVerb) return false;
      if (!query) return true;
      return (
        group.name.toLowerCase().includes(query) ||
        group.endpoints.some((endpoint) =>
          providerLabel(endpoint.attributes?.name)
            .toLowerCase()
            .includes(query),
        )
      );
    });
  }, [groups, search, activeVerb]);

  // Keep a valid selection: default to the first group and recover if the
  // current selection disappears (e.g. once data finishes loading).
  useEffect(() => {
    if (groups.length === 0) return;
    if (!selectedId || !groups.some((group) => group.id === selectedId)) {
      setSelectedId(groups[0].id);
    }
  }, [groups, selectedId]);

  const selectedGroup = useMemo(
    () => groups.find((group) => group.id === selectedId) || null,
    [groups, selectedId],
  );

  if (loading) {
    return <SkeletonView />;
  }

  return (
    <div className="at-root">
      <Helmet
        title={t`Decisiv Integration Lambda`}
        description={t`Decisiv Integration Lambda`}
      />
      <div className="page">
        <Toolbar
          search={search}
          onSearch={setSearch}
          verbs={verbs}
          activeVerb={activeVerb}
          onVerb={setActiveVerb}
        />
        <div className="body">
          <ListPane
            groups={filteredGroups}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
          {selectedGroup ? (
            <DetailPane group={selectedGroup} />
          ) : (
            <EmptyDetail />
          )}
        </div>
      </div>
    </div>
  );
}
